import { Link } from "react-router-dom";

type LogoSize = "sm" | "md" | "lg";
type LogoVariant = "default" | "on-dark" | "on-brand";

const MARK_FILL = "#f97316";

interface CravbotMarkProps {
  size?: number;
  className?: string;
}

/**
 * App icon: solid orange tile + chat bubble with fork tines (AI ordering).
 * Not a letter — distinct from the wordmark C.
 */
export function CravbotMark({ size = 36, className = "" }: CravbotMarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      <rect width="40" height="40" rx="11" fill={MARK_FILL} />
      {/* Chat bubble */}
      <path
        d="M11 13.5c0-2.2 1.8-4 4-4h10c2.2 0 4 1.8 4 4v7.5c0 2.2-1.8 4-4 4H21.2L17 30.2V25H15c-2.2 0-4-1.8-4-4v-7.5Z"
        fill="white"
      />
      {/* Fork tines — restaurant */}
      <path
        d="M17.2 12.8V17M20 12.8V17M22.8 12.8V17"
        stroke={MARK_FILL}
        strokeWidth="1.65"
        strokeLinecap="round"
      />
      <path
        d="M16.6 17h7.8"
        stroke={MARK_FILL}
        strokeWidth="1.65"
        strokeLinecap="round"
      />
      {/* Order lines — conversation flow */}
      <path
        d="M15.5 20.5h11M15.5 23h8"
        stroke={MARK_FILL}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Wordmark "c" — plain glyph (custom styling paused). */
export function CravbotLetterC({ className = "" }: { className?: string }) {
  return <span className={className}>c</span>;
}

interface CravbotWordmarkProps {
  size?: LogoSize;
  variant?: LogoVariant;
  className?: string;
}

const wordmarkSizeMap: Record<LogoSize, string> = {
  sm: "text-lg sm:text-xl",
  md: "text-2xl",
  lg: "text-4xl",
};

function cravColor(variant: LogoVariant): string {
  if (variant === "on-dark") return "text-brand-400";
  if (variant === "on-brand") return "text-white";
  return "text-brand-500";
}

function botColor(variant: LogoVariant): string {
  if (variant === "on-dark") return "text-white";
  return "text-gray-900";
}

/** Wordmark: custom C + rav (orange) + bot (dark) */
export function CravbotWordmark({
  size = "md",
  variant = "default",
  className = "",
}: CravbotWordmarkProps) {
  const crav = cravColor(variant);
  const bot = botColor(variant);

  return (
    <span
      aria-label="cravbot"
      className={`font-extrabold tracking-tight leading-none select-none whitespace-nowrap ${wordmarkSizeMap[size]} ${className}`}
    >
      <CravbotLetterC className={crav} />
      <span className={crav} aria-hidden>
        rav
      </span>
      <span className={bot} aria-hidden>
        bot
      </span>
    </span>
  );
}

interface LogoProps {
  size?: LogoSize;
  withText?: boolean;
  withMark?: boolean;
  variant?: LogoVariant;
  className?: string;
  markClassName?: string;
  textClassName?: string;
  href?: string;
}

const markSizeMap: Record<LogoSize, number> = { sm: 36, md: 36, lg: 48 };

export function Logo({
  size = "md",
  withText = true,
  withMark = true,
  variant = "default",
  className = "",
  markClassName = "",
  textClassName = "",
  href,
}: LogoProps) {
  const content = (
    <div className={`inline-flex items-center gap-2.5 ${className}`}>
      {withMark && (
        <CravbotMark
          size={markSizeMap[size]}
          className={`shrink-0 ${markClassName}`}
        />
      )}
      {withText && <CravbotWordmark size={size} variant={variant} className={textClassName} />}
    </div>
  );

  if (href) {
    return (
      <Link to={href} className="inline-flex group" aria-label="Cravbot home">
        {content}
      </Link>
    );
  }

  return content;
}
